/**
 * Service Requests Page
 * @description 서비스 요청 목록 페이지 (Service Requests, Incidents, Change Requests)
 */

import { useState } from 'react';
import { useNavigate } from '@shared/router';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { Plus } from 'lucide-react';
import { TicketList } from '../components/TicketList';
import type { TicketType } from '@/types/ticket.types';

type TabValue = 'all' | 'my' | 'assigned' | 'approval';

interface ServiceRequestsPageProps {
    ticketType?: TicketType;
}

const ticketTypeLabels: Record<TicketType, { title: string; description: string; newRoute: string }> = {
    servicerequest: {
        title: '서비스 요청',
        description: 'Service Requests 관리',
        newRoute: '/it-service/servicerequests/new',
    },
    incident: {
        title: '장애 관리',
        description: 'Incidents 관리',
        newRoute: '/it-service/incidents/new',
    },
    changerequest: {
        title: '변경 관리',
        description: 'Change Requests 관리',
        newRoute: '/it-service/changerequests/new',
    },
};

export const ServiceRequestsPage: React.FC<ServiceRequestsPageProps> = ({
    ticketType = 'servicerequest'
}) => {
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const [activeTab, setActiveTab] = useState<TabValue>('all');

    const config = ticketTypeLabels[ticketType];

    if (user?.userType !== 'tenant-user') {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Tenant 사용자만 접근 가능합니다.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">{config.title}</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {config.description}
                    </p>
                </div>
                <Button
                    onClick={() => navigate({ to: config.newRoute as any })}
                    className="gap-2"
                >
                    <Plus className="w-4 h-4" />
                    새로 만들기
                </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
                <TabsList>
                    <TabsTrigger value="all">전체</TabsTrigger>
                    <TabsTrigger value="my">내 티켓</TabsTrigger>
                    <TabsTrigger value="assigned">담당 티켓</TabsTrigger>
                    <TabsTrigger value="approval">승인 대기</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-6">
                    <TicketList
                        tenantId={user.tenantId}
                        ticketType={ticketType}
                    />
                </TabsContent>

                <TabsContent value="my" className="mt-6">
                    <TicketList
                        tenantId={user.tenantId}
                        ticketType={ticketType}
                        defaultFilters={{ reporter: user.userId }}
                    />
                </TabsContent>

                <TabsContent value="assigned" className="mt-6">
                    <TicketList
                        tenantId={user.tenantId}
                        ticketType={ticketType}
                        defaultFilters={{ assignee: user.userId }}
                    />
                </TabsContent>

                <TabsContent value="approval" className="mt-6">
                    <TicketList
                        tenantId={user.tenantId}
                        ticketType={ticketType}
                        defaultFilters={{ status: 'pending' }}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

ServiceRequestsPage.displayName = 'ServiceRequestsPage';
